import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { ThemeColours } from 'fym-shared/src/music24/releaseThemes';
import { SPOTIFY_HEADER_SIZE, SPOTIFY_HEADER_ZONES } from 'fym-shared/src/music24/spotifyHeader';
import { profileImageWatermark, spotifyHeaderWatermark } from 'fym-shared/src/music24/story/watermark';
import { TypedMemo } from '../../../../common/types';
import { WatermarkLockup } from '../frames/WatermarkLockup';
import { drawnGroundKind, FrameGround, type HeaderPreviewGround } from './FrameGround';
import { nameOverlay, previewScale, viewBox, zoneBox, type HeaderView, type PxBox } from './spotifyHeaderGeometry';
import { spotifyHeaderMessages as messages } from './spotifyHeaderMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Spotify header; each part uses its own slice of it */
import styles from './SpotifyHeader.module.scss';
/* eslint-enable css-modules/no-unused-class */

export type { HeaderPreviewGround } from './FrameGround';

interface SpotifyHeaderFrameProps {
  /** The width of the preview in px. */
  width: number;
  view: HeaderView;
  ground: HeaderPreviewGround;
  colours: ThemeColours;
  /** The round photo and the name that Spotify draws; null or absent: none. */
  overlay?: { artist: string; avatarUrl: string | null } | null;
  /** The crop guides: the phone band, the desktop rows, the subject box. Never in the file. */
  guides?: boolean;
  /** A Free file: the Music24 lockup where the render puts it. */
  watermark?: boolean;
  /**
   * The place of the lockup of another file of the same size (the artist Profile header,
   * `ProfileImageSpec.watermark`). Absent: the place of the kit Spotify header.
   */
  watermarkPlace?: { L: number; right: number; bottom: number };
  className?: string;
  testId?: string;
}

const { width: W, height: H } = SPOTIFY_HEADER_SIZE;

const boxStyle = (box: PxBox): React.CSSProperties => ({
  left: box.left,
  top: box.top,
  width: box.width,
  height: box.height,
});

/**
 * One Spotify header preview (design spotify-header `HeaderFrame`): the file drawn at file pixels
 * (2660 x 1140) and scaled into `width`, with the same ground, crop and watermark place as the
 * render (fym-shared `spotifyHeader.ts`, music24-api `renderSpotifyHeader`). The phone view shows
 * the middle half. The overlay and the guides show only in the preview, never in the file.
 */
const SpotifyHeaderFrameComponent: React.FC<SpotifyHeaderFrameProps> = ({
  width,
  view,
  ground,
  colours,
  overlay = null,
  guides = false,
  watermark = false,
  watermarkPlace,
  className,
  testId,
}) => {
  const intl = useIntl();
  const { scale, height } = previewScale(view, width);
  const box = viewBox(view);
  const mark = useMemo(() => {
    if (!watermark) {
      return null;
    }
    return watermarkPlace === undefined
      ? spotifyHeaderWatermark(SPOTIFY_HEADER_SIZE)
      : profileImageWatermark(SPOTIFY_HEADER_SIZE, watermarkPlace, W);
  }, [watermark, watermarkPlace]);
  const name = nameOverlay(view);

  return (
    <div
      className={clsx(styles.frame, className)}
      style={{ width, height }}
      data-testid={testId}
      data-view={view}
      data-ground={drawnGroundKind(ground)}
      role="img"
      aria-label={intl.formatMessage(messages.previewAlt)}
    >
      <div
        className={styles.canvas}
        style={{ width: W, height: H, transform: `scale(${scale}) translateX(${-box.left}px)` }}
      >
        <FrameGround frame={SPOTIFY_HEADER_SIZE} ground={ground} colours={colours} />
        {mark !== null && <WatermarkLockup lockup={mark.lockup} tone="dark" x={mark.x} y={mark.y} />}
        {guides && (
          <>
            {view !== 'phone' && (
              <>
                <div
                  className={styles.shade}
                  style={{ left: 0, top: 0, width: zoneBox(SPOTIFY_HEADER_ZONES.phone).left, height: H }}
                />
                <div
                  className={styles.shade}
                  style={{
                    left: zoneBox(SPOTIFY_HEADER_ZONES.phone).left + zoneBox(SPOTIFY_HEADER_ZONES.phone).width,
                    top: 0,
                    right: 0,
                    height: H,
                  }}
                />
                <div className={styles.cut} style={boxStyle(zoneBox(SPOTIFY_HEADER_ZONES.phone))} />
              </>
            )}
            <div className={styles.rowShade} style={{ top: 0, height: zoneBox(SPOTIFY_HEADER_ZONES.desktop).top }} />
            <div
              className={styles.rowShade}
              style={{
                top: zoneBox(SPOTIFY_HEADER_ZONES.desktop).top + zoneBox(SPOTIFY_HEADER_ZONES.desktop).height,
                bottom: 0,
              }}
            />
            <div className={styles.safe} style={boxStyle(zoneBox(SPOTIFY_HEADER_ZONES.subject))}>
              <span className={styles.safeLabel}>{intl.formatMessage(messages.safeLabel)}</span>
            </div>
          </>
        )}
        {overlay !== null && (
          <div
            className={styles.name}
            style={{ left: name.left, bottom: name.bottom }}
            data-testid="spotify-header-overlay"
          >
            <span
              className={styles.avatar}
              style={{
                width: name.avatar,
                height: name.avatar,
                backgroundImage: overlay.avatarUrl === null ? undefined : `url("${overlay.avatarUrl}")`,
              }}
            />
            <span className={styles.nameText}>
              <span className={styles.verified}>{intl.formatMessage(messages.verified)}</span>
              <b className={styles.artist} style={{ fontSize: name.nameSize }}>
                {overlay.artist}
              </b>
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export const SpotifyHeaderFrame = TypedMemo(SpotifyHeaderFrameComponent);
